import React, { useState } from 'react'
import {Check, Loader2, Rocket, Star} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface PricingPageProps {
  currentUserId?: string
  user?: any
  onNavigate?: (view: string) => void
  onSignIn?: () => void
}

export function PricingPage({ currentUserId, user, onNavigate, onSignIn }: PricingPageProps) {
  const [loading, setLoading] = useState<string | null>(null)

  const handleSubscribe = async (subscriptionType: 'commenter' | 'project_owner') => {
    if (!currentUserId) {
      alert('Please sign in to subscribe')
      return
    }

    setLoading(subscriptionType)

    try {
      const response = await lumi.functions.invoke('createCheckoutSession', {
        method: 'POST',
        body: {
          ownerId: currentUserId,
          subscriptionType
        }
      })

      if (response.url) {
        // Open in new tab then redirect
        const win = window.open()
        if (win) {
          win.location.href = response.url
        }
      }
    } catch (error: any) {
      console.error('Checkout error:', error)
      alert(error?.data?.error || 'Failed to create checkout session')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-300">
            Join the Base Builders community and showcase your project
          </p>
        </div>

        {/* User Profiles Plans */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">USER PROFILES</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 hover:border-white/20 transition-all">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
              <div className="text-4xl font-bold text-white mb-2">$0</div>
              <p className="text-gray-400">Forever free</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Browse all projects</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span>View project profiles</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span>Explore featured projects</span>
              </li>
            </ul>

            <button
              disabled
              className="w-full py-3 bg-white/10 text-white rounded-lg font-medium cursor-not-allowed"
            >
              Current Plan
            </button>
          </div>

            {/* Commenter Plan */}
            <div className="bg-gradient-to-br from-[#0052FF]/20 to-[#60A5FA]/20 border-2 border-[#0052FF] rounded-xl p-8 relative hover:shadow-xl hover:shadow-[#0052FF]/50 transition-all">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="px-4 py-1 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white text-sm font-bold rounded-full">
                POPULAR
              </span>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Commenter</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-4xl font-bold text-white">$5</span>
                <span className="text-gray-400">/month</span>
              </div>
              <p className="text-gray-400">Engage with the community</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#0052FF] flex-shrink-0 mt-0.5" />
                <span>All Free features</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#0052FF] flex-shrink-0 mt-0.5" />
                <span>Like photos and videos</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#0052FF] flex-shrink-0 mt-0.5" />
                <span>Comment on media</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#0052FF] flex-shrink-0 mt-0.5" />
                <span>Review projects with 🚀 ratings</span>
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('commenter')}
              disabled={loading === 'commenter'}
              className="w-full py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading === 'commenter' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                'Subscribe Now'
              )}
            </button>
            </div>
          </div>
        </div>

        {/* Projects Plans */}
        <div className="mt-16 py-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center px-4">PROJECTS</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Project Owner Plan */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 hover:border-[#FFD700]/50 transition-all">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Project Owner</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-4xl font-bold text-white">$20</span>
                <span className="text-gray-400">/month</span>
              </div>
              <p className="text-[#00D9FF] font-semibold mb-1">🎉 1 Month Free Trial</p>
              <p className="text-gray-400 text-sm">Payment info required • No charge for 30 days</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>All Commenter features</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>List your project</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>Custom banner & profile</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>Media gallery (photos & videos)</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>Social links & website</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>Blockchain & contract info</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <Check className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span>News Feed access</span>
              </li>
              <li className="flex items-start gap-2 text-pink-400">
                <Star className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5 fill-pink-400" />
                <span>Feature your project</span>
              </li>
            </ul>

            <button
              onClick={() => handleSubscribe('project_owner')}
              disabled={loading === 'project_owner'}
              className="w-full py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading === 'project_owner' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  List Project
                </>
              )}
            </button>
            </div>

            {/* Featured Project Plan */}
            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-2 border-purple-500 rounded-xl p-8 relative hover:shadow-xl hover:shadow-purple-500/50 transition-all">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  BOOST VISIBILITY
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Featured</h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-4xl font-bold text-white">$15+</span>
                  <span className="text-gray-400">/one-time</span>
                </div>
                <p className="text-gray-400">One-time boost</p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span>Featured badge display</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span>Priority placement</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span>Flexible tiers (3-30 days)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span>Maximum visibility</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span>Requires project listing</span>
                </li>
              </ul>

              {user ? (
                <button
                  onClick={() => {
                    if (onNavigate) {
                      onNavigate('dashboard')
                      setTimeout(() => {
                        const section = document.getElementById('feature-project-section')
                        if (section) section.scrollIntoView({ behavior: 'smooth' })
                      }, 100)
                    }
                  }}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/50 flex items-center justify-center gap-2"
                >
                  <Star className="w-5 h-5" />
                  Feature My Project
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (onSignIn) {
                      onSignIn()
                    }
                  }}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/50 flex items-center justify-center gap-2"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">How does the 1-month free trial work?</h3>
              <p className="text-gray-400">Project listings include a 30-day free trial. You'll enter payment info when listing, but won't be charged until after the trial ends.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-400">Yes! You can cancel your subscription at any time through your Stripe customer portal.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">What happens if I cancel?</h3>
              <p className="text-gray-400">Your access continues until the end of your billing period. After that, you'll return to the free plan.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Can I upgrade from Commenter to Project Owner?</h3>
              <p className="text-gray-400">Yes! You can upgrade at any time. The price difference will be prorated.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
